import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  useGetAllStudentsBySchoolMutation,
  useGetEventsBySchoolMutation,
  useGetStudentsByEventIdsQuery,
  useResetPasswordToDefaultMutation,
  useDeleteStudentsMutation,
  useGetSchoolDetailsByIdMutation,
  useExportStudentsByEventMutation,
  useGetGlobalEventsQuery,
} from "./studentMutation";
import { formatDateToMonthDDYYYYTimeFormat } from "../../shared/utils/formatDate";
import {
  setSchoolUploadPermission,
  setEventUploadPermission,
  resetUploadPermissions,
} from "./uploadPermissions.slice";

export const useEventStudentsQueries = (
  initialPage = 1,
  initialLimit = 100,
  initialAttemptFilter = "ALL",
  eventIds = null
) => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [attemptFilter, setAttemptFilter] = useState(initialAttemptFilter);

  const { data, isLoading, error, refetch, isFetching } =
    useGetStudentsByEventIdsQuery(
      {
        event_ids: eventIds,
        page,
        limit,
        attempt_filter: attemptFilter,
      },
      {
        skip: !eventIds,
        refetchOnMountOrArgChange: true,
      }
    );

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(Math.min(Math.max(newLimit, 1), 2000));
    setPage(1);
  };

  const handleAttemptFilterChange = (newFilter) => {
    setAttemptFilter(newFilter.toUpperCase());
    setPage(1);
  };

  const formattedStudents =
    data?.students?.map((student) => ({
      name:
        (student.student_details?.first_name || "") +
        (student.student_details?.last_name
          ? ` ${student.student_details.last_name}`
          : ""),
      crn_id: student.crn_id || "",
      school_id: student.student_details?.school_id || "",
      event_id: student.event_id || "",
      isd_code: student.student_details?.isd_code || "",
      phone: student.student_details?.phone || "",
      grade: student.student_details?.grade_id || "",
      loginId: student.student_details?.admission_id || "",
      examName: student.event_details?.exam_name || "",
      school_name: student.student_details?.school_name || "",
      regSource: student.student_details?.registration_source || "",
      registeredOn:
        formatDateToMonthDDYYYYTimeFormat(
          student.student_details?.registration_date
        ) || "",
      attempt: student.exam_attempted ? "Yes" : "No",
      certificateLink: student.student_report?.certificate || "",
      reportLink: student.student_report?.report || "",
      basicReportLink: student.student_report?.report_pdf_link || "",
    })) || [];

  return {
    eventStudents: formattedStudents,
    totalCount: data?.totalCount || 0,
    attemptedCount: data?.attemptedCount || 0,
    currentPage: page,
    itemsPerPage: limit,
    attemptFilter,
    isLoading: isLoading || isFetching,
    error: error
      ? {
          message: error.message || "Failed to fetch students",
        }
      : null,
    handlePageChange,
    handleLimitChange,
    handleAttemptFilterChange,
    refetch,
  };
};

export const useGlobalEvents = (level, options) => {
  const levelFormatted =
    level &&
    level
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");
  const {
    data: events,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetGlobalEventsQuery(
    {
      page: 1,
      limit: 99,
      level: levelFormatted,
    },
    options
  );

  return {
    events: events || [],
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};

const useStudentsPageQueries = () => {
  const dispatch = useDispatch();
  const [getAllStudentsBySchool] = useGetAllStudentsBySchoolMutation();
  const [getEventsBySchool] = useGetEventsBySchoolMutation();
  const [resetPasswordToDefault] = useResetPasswordToDefaultMutation();
  const [deleteStudents] = useDeleteStudentsMutation();
  const [getSchoolDetailsById] = useGetSchoolDetailsByIdMutation();
  const [exportStudentsByEvent] = useExportStudentsByEventMutation();

  const handleGetAllStudentsBySchool = async (schoolIds) => {
    try {
      dispatch(resetUploadPermissions());

      const students = await getAllStudentsBySchool({ schoolIds }).unwrap();
      return students;
    } catch (error) {
      console.error("Error fetching students by school", error);
      dispatch(setSchoolUploadPermission(false));
      if (error.status === 403 || error?.data?.status_code === 403) {
        throw new Error(
          error?.data?.message || "You are not authorized to access this school"
        );
      }
      throw new Error("Failed to fetch students");
    }
  };

  const handleGetEventsBySchool = async (schoolId, page = 1, limit = 99) => {
    try {
      dispatch(resetUploadPermissions());

      const events = await getEventsBySchool({
        schoolId,
        page,
        limit,
      }).unwrap();
      return events;
    } catch (error) {
      console.error("Error fetching events by school", error);
      dispatch(setEventUploadPermission(false));
      if (error.status === 403 || error?.data?.status_code === 403) {
        throw new Error(
          error?.data?.message || "You are not authorized to access this school"
        );
      }
      throw new Error("Failed to fetch events");
    }
  };

  // Removed handleGetStudentsByEventIds since we're using the query hook directly in useEventStudentsQueries

  const handleResetPasswordToDefault = async (admission_ids) => {
    try {
      const response = await resetPasswordToDefault({ admission_ids }).unwrap();
      if (response.error) {
        throw response.error;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error resetting passwords to default", error);
      throw error;
    }
  };

  const handleDeleteStudents = async (admission_ids) => {
    try {
      const response = await deleteStudents({ admission_ids }).unwrap();
      if (response.error) {
        throw response.error;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error deleting students", error);
      throw error;
    }
  };

  const handleGetSchoolDetailsById = async (schoolId) => {
    try {
      const response = await getSchoolDetailsById({ schoolId }).unwrap();
      return response;
    } catch (error) {
      console.error("Error fetching school details by id", error);
      if (error.status === 403 || error?.data?.status_code === 403) {
        throw new Error(
          error?.data?.message || "You are not authorized to access this school"
        );
      }
      throw error;
    }
  };

  const handleExportStudentsByEvent = async (
    eventIds,
    attemptFilter,
    exportFormat,
    isGlobal = false
  ) => {
    try {
      const response = await exportStudentsByEvent({
        event_ids: eventIds,
        attempt_filter: attemptFilter || "ALL",
        export_format: exportFormat || "csv",
        isGlobal,
      }).unwrap();
      return response;
    } catch (error) {
      console.error("Error exporting students by event", error);
      throw error;
    }
  };

  return {
    handleGetAllStudentsBySchool,
    handleGetEventsBySchool,
    handleResetPasswordToDefault,
    handleDeleteStudents,
    handleGetSchoolDetailsById,
    handleExportStudentsByEvent,
  };
};

export default useStudentsPageQueries;
